import api from './api';
import type {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
  ClientFilters,
  PaginatedResponse,
} from '../types';

export const clientService = {
  async getAll(params?: ClientFilters): Promise<PaginatedResponse<Client>> {
    const response = await api.get('/clients', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    // Normalize to PaginatedResponse shape that components expect
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 20,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    // If response is already in expected shape or is an array
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 20,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },

  async getById(id: string): Promise<Client> {
    const response = await api.get<Client>(`/clients/${id}`);
    return response.data;
  },

  async create(data: CreateClientRequest): Promise<Client> {
    const response = await api.post<Client>('/clients', data);
    return response.data;
  },

  async update(id: string, data: UpdateClientRequest): Promise<Client> {
    const response = await api.put<Client>(`/clients/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/clients/${id}`);
  },

  async getCSATHistory(id: string): Promise<{ date: string; score: number }[]> {
    const response = await api.get(`/clients/${id}/csat-history`);
    const raw = response.data;
    // API may return array or { data: [...] }
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  },
};
